import { computed, h } from 'vue';
import State from '@/state/State';
import IntegrationsState from '@/state/IntegrationsState';
import { getPartnershipData } from '@/utils/partnership';

const { featureFlags: partnershipFeatureFlags } = getPartnershipData();

// Preview mode - check URL params and persist to sessionStorage
const PREVIEW_2026_METRICS_KEY = 'preview_2026_metrics';

// Eagerly persist ALL preview params at module load so they survive navigation
if (typeof window !== 'undefined') {
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.has('preview_2026_metrics')) {
    sessionStorage.setItem(PREVIEW_2026_METRICS_KEY, 'true');
  }
}

// Helper for portal_updates_jan_2026 feature flag
const isPortalUpdatesJan2026Enabled = () => {
  if (typeof window !== 'undefined' && sessionStorage.getItem(PREVIEW_2026_METRICS_KEY) === 'true') {
    return true;
  }
  return State?.state?.company?.properties?.app_features?.portal_updates_jan_2026_version === 'v1';
};

const isFlagEnabled = flag =>
  computed(
    () =>
      State.state.company?.properties?.b2b_features?.variations_whitelist?.includes(flag) ||
      State.state.company?.properties?.portal?.[flag] ||
      partnershipFeatureFlags?.[flag] ||
      false,
  );

const isSme = computed(() => State.state.company?.properties?.Author === 'company_registration');

const flags = {
  canAddEmployees: isFlagEnabled('CAN_ADD_EMPLOYEES'),
  canAddSingleEmployee: isFlagEnabled('CAN_ADD_SINGLE_EMPLOYEE'),
  canAddShifts: isFlagEnabled('CAN_ADD_SHIFTS'),
  canAddSingleShift: isFlagEnabled('CAN_ADD_SINGLE_SHIFT'),
  canViewAbsences: isFlagEnabled('CAN_VIEW_ABSENCES'),
  hideShiftsRate: isFlagEnabled('HIDE_SHIFTS_HOURLY_RATE'),
  canAddAbsences: isFlagEnabled('CAN_ADD_ABSENCES'),
  canViewJobs: isFlagEnabled('CAN_VIEW_JOBS'),
  // Off = the existing fixed-operator filters; needs shifts-v2 on the backend.
  filterOperatorsEnabled: isFlagEnabled('filter_operators_enabled'),
  canAddJobs: isFlagEnabled('CAN_ADD_JOBS'),
  canEnrollEmployees: computed(
    () =>
      isFlagEnabled('CAN_ENROLL_EMPLOYEES').value &&
      // todo check this logic, should soft launch be able to enroll employees?
      (State.state?.company?.properties?.is_soft_launch || State.state?.company?.partner_state === 'LIVE'),
  ),
  canDisableEmployees: isFlagEnabled('CAN_DISABLE_EMPLOYEES'),
  canTerminateEmployees: isFlagEnabled('CAN_TERMINATE_EMPLOYEES'),
  canUpdateBanking: isFlagEnabled('CAN_UPDATE_BANKING'),
  canIntegrate: isFlagEnabled('CAN_INTEGRATE'),
  canUpdatePayschedule: isFlagEnabled('CAN_UPDATE_PAYSCHEDULE'),
  canLaunchSelf: isFlagEnabled('CAN_LAUNCH_SELF'),
  canSalarySignOff: isFlagEnabled('CAN_SALARY_SIGN_OFF'),
  canSignTerms: isFlagEnabled('CAN_SIGN_TERMS'),
  isSubscription: isFlagEnabled('IS_SUBSCRIPTION'),
  isKYC: isFlagEnabled('IS_KYC'),
  isSelfFunded: isFlagEnabled('IS_SELF_FUNDED'),
  isDisablePayscheduleEditing: computed(
    () => isFlagEnabled('disable_payschedule_editing').value || !isFlagEnabled('CAN_UPDATE_PAYSCHEDULE').value,
  ),
  canBulkUploadPayscheduleDates: isFlagEnabled('CAN_BULK_UPLOAD_PAYSCHEDULE_DATES'),
  isDisableIntegratedEmployeeFieldsStrict: isFlagEnabled('disable_integrated_employee_fields_strict'),
  isDisableLandingPageLinks: isFlagEnabled('disable_landing_page_links'),
  isDisablePartnerLogos: isFlagEnabled('disable_partner_logos'),
  isDisablePayWithWagestream: isFlagEnabled('disable_pay_with_wagestream'),
  isDisableRegistration: isFlagEnabled('disable_registration'),
  isDisableOffCyclePayments: isFlagEnabled('disable_off_cycle_payments'),
  isDisablePortalReferrals: isFlagEnabled('disable_portal_referrals'),
  isEnableEmployeePauseScheduling: isFlagEnabled('enable_employee_pause_scheduling'),
  isEnableEmployeeForm: isFlagEnabled('enable_employee_form'),
  isEnableExpandedEmployeeStreamingFormFields: isFlagEnabled('enable_expanded_employee_streaming_form_fields'),
  isEnableI18n: isFlagEnabled('i18n_enabled'),
  isEnableHideDisabled: isFlagEnabled('hide_disabled'),
  isEnableHideNonEnrolled: isFlagEnabled('hide_non_enrolled'),
  isEnableStreamedWagesPage: isFlagEnabled('show_streamed_wages_page'),
  isEnableShowPayschedulesOnStreamedWagesPage: isFlagEnabled('show_payschedules_on_streamed_wages_page'),
  isEnableLumpSumPayPage: isFlagEnabled('enable_lump_sum_pay'),
  isEnableEmployeePaymentsPage: isFlagEnabled('show_employee_payments_report'),
  isEnableReportingPage: isFlagEnabled('show_reporting_page'), // US-only portal flag
  isFundingPositionPageDisabled: isFlagEnabled('CANNOT_VIEW_FUNDING_POSITION'),
  isEnableShowBankingFile: isFlagEnabled('show_banking_file'),
  isEnableWhatsApp: computed(() => State.state.company?.properties?.b2b_features?.whatsapp_comms || isSme.value),
  isEnableAdoptionGamification: computed(
    () => State.state.company?.properties.b2b_features?.adoption_gamification || isSme.value,
  ),
  isUseUserCountInsteadOfEmployeesCount: computed(
    () => State.state.company?.properties?.dashboards_count_users || false,
  ),
  // a singular pay_day ensures only weekly pay companies can see their history
  isEnableBankPayHistory: computed(
    () =>
      (State.state.company?.properties?.weekly_pay_settings?.enabled &&
        State.state.company?.properties?.weekly_pay_settings?.pay_day) ||
      false,
  ),
  isAddAdminDisabledInSSO: computed(
    // US feature flag
    () =>
      (isFlagEnabled('disable_admin_add_if_sso') &&
        IntegrationsState.state.integrations?.find(integration => integration.standard === 'OPENID')) ||
      false,
  ),
  disableAdminCreationViaPortal: isFlagEnabled('DISABLE_ADMIN_CREATION_VIA_PORTAL'), // UK feature flag
  isSme: isSme,
  canViewIntegrationHistory: isFlagEnabled('CAN_VIEW_INTEGRATIONS_HISTORY'),
  shouldSignDbTermsAddendum: computed(
    () => State?.state?.company?.properties?.should_accept_salary_service_addendum_db || false,
  ),
  allowEditingAddress: isFlagEnabled('CAN_EDIT_EMPLOYEE_ADDRESS'),
  selfServicePhoneEnrolment: isFlagEnabled('self_service_phone_enrolment'),
  enforceMfaSetup: isFlagEnabled('enforce_mfa_setup'),
  canSetAccountType: isFlagEnabled('CAN_SET_ACCOUNT_TYPE'),
  canSetDefaultWagerate: isFlagEnabled('CAN_SET_DEFAULT_WAGERATE'),
  displayNetPayShifts: isFlagEnabled('DISPLAY_NET_PAY_SHIFTS'),
  showLeaversAsDisabled: isFlagEnabled('SHOW_LEAVERS_AS_DISABLED'),
  hideShiftsHoursAndRate: isFlagEnabled('hide_shifts_hours_and_rate'),
  hideEmployeeOptOut: isFlagEnabled('hide_optout'),
  hideEmployeePause: isFlagEnabled('hide_pause'),
  isEnableSdl: computed(
    () => isFlagEnabled('sdl_enabled').value || State?.state?.company?.properties?.app_features?.sdl_enabled || false,
  ),
  hideStreamAccountRelatedItems: isFlagEnabled('hide_stream_account_related_items'),
  showAchFailedSalaryExplainer: isFlagEnabled('show_ach_failed_salary_explainer'), // US feature flag
  shouldShowReferralsAdvertisment: isFlagEnabled('show_referrals_advertisment'),
  isDemoCompany: computed(() => State.state.company?.properties?.is_demo || false),
  isEnableSmeLaunchV2: computed(
    () => State.state.company?.properties?.sme_properties?.launching_with_launch_v2 || false,
  ),
  isUkCompany: computed(() => (State.state.company?.properties?.payment_country_code || 'GB') === 'GB'),
  isUsCompany: computed(() => ['US', 'CA'].includes(State.state.company?.properties?.payment_country_code)),
  isNhsCompany: computed(() => Boolean(State.state.company?.properties?.nhs_trust_code)),
  isDisableSelfServiceReinviteEmail: computed(
    () => State.state.company?.properties?.employee_comms?.disable_self_service_reinvite_email || false,
  ),
  isEnableLastDayPay: computed(() => {
    if (
      ['v2', 'standalone'].includes(State?.state?.company?.properties?.off_cycle_payments?.off_cycle_payments_version)
    ) {
      return false;
    }
    return isFlagEnabled('last_day_pay_enabled').value;
  }),
  isEnableLastDayPayV3: computed(() => {
    if (
      ['v2', 'standalone'].includes(State?.state?.company?.properties?.off_cycle_payments?.off_cycle_payments_version)
    ) {
      return false;
    }
    return isFlagEnabled('last_day_pay_enabled').value && isFlagEnabled('last_day_pay_v3_enabled').value;
  }),
  offCyclePaymentsVersion: computed(
    () => State?.state?.company?.properties?.off_cycle_payments?.off_cycle_payments_version || 'off',
  ),
  recognitionVersion: computed(() => State?.state?.company?.properties?.app_features?.recognition_version || 'off'),
  // Company-level version flag from paydays-api app_features; gates the Policies page that
  // feeds Coach's workplace-policy answers.
  coachPolicyDocumentsVersion: computed(
    () => State?.state?.company?.properties?.app_features?.coach_policy_documents_version || 'off',
  ),
  deductionsPortalVersion: computed(
    () => State?.state?.company?.properties?.app_features?.deductions_portal_version || 'off',
  ),
  recognitionBudgetsVersion: computed(
    () => State?.state?.company?.properties?.app_features?.recognition_budgets_version || 'off',
  ),
  // 'entire_company' preselects company-wide scope on the create-award forms (the old
  // behaviour, kept for clients with few divisions); 'off' forces an explicit choice.
  recognitionAwardDefaultDivisionScope: computed(
    () => State?.state?.company?.properties?.portal?.recognition_award_default_division_scope || 'off',
  ),
  shouldAcceptDbTermsOfBusinessAmendment: computed(
    () => State?.state?.company?.properties?.should_accept_db_terms_of_business_amendment || false,
  ),
  shouldAcceptSalaryServiceAddendumDb: computed(
    () => State?.state?.company?.properties?.should_accept_salary_service_addendum_db || false,
  ),
  shouldAcceptMhrTermsOfBusinessAmendment: computed(
    () => State?.state?.company?.properties?.should_accept_mhr_terms_of_business_amendment || false,
  ),
  shouldAcceptHealthcareTermsOfBusinessAmendment: computed(
    () => State?.state?.company?.properties?.should_accept_healthcare_terms_of_business_amendment || false,
  ),
  isNetEarningsEnabled: computed(
    () => State?.state?.company?.properties?.app_features?.net_earnings_enabled == 'v1' || false,
  ),
  delayedOffCyclePayments: computed(
    () => State?.state?.company?.properties?.off_cycle_payments?.delayed_payments_enabled || false,
  ),
  hideInvoicePage: isFlagEnabled('hide_invoice_page'),
  hideDashboardShifts: isFlagEnabled('hide_dashboard_shifts'),
  hideShiftsNav: isFlagEnabled('hide_shifts_nav'),
  showDeductionsFiles: isFlagEnabled('show_deductions_files'),
  usingExternalIdAsEmployeeCode: isFlagEnabled('external_id_as_employee_code'),
  shouldAcceptLoopAgreement: computed(() => State?.state?.company?.properties?.should_accept_loop_agreement || false),
  shouldAcceptW2UpgradeNotification: computed(
    () => State?.state?.company?.properties?.should_accept_w2_upgrade_notification || false,
  ),
  addDetailedEmployeeDataToExport: isFlagEnabled('add_detailed_employee_data_to_export'),
  shouldAcceptMyviewPayNowAmendment: computed(
    () => State?.state?.company?.properties?.should_accept_myview_pay_now_amendment || false,
  ),
  tescoPortalStubsEnabled: computed(() => State?.state?.company?.properties?.tesco_demo_portal_stubs_enabled || false),
  portalUpdatesJan2026Enabled: computed(() => isPortalUpdatesJan2026Enabled()),
  isSafeguardingEnabled: isFlagEnabled('SAFEGUARDING_ANALYTICS'),
  // The adoption endpoint only carries a `roamers` series for some companies, so the tile stays
  // off until a company has the data. Enable per company via variations_whitelist/portal, or for
  // every US company at once via the wagestreamus partnership flags in utils/partnership.js.
  showRoamersTile: isFlagEnabled('show_roamers_tile'),
  // The platform sets the company's Modulr GBP sort code through the feature flag hierarchy, so
  // the value sits in app_features beside the staged flags. An absent key means the old shared
  // range. Consumers compare the value to a sort code, so this returns the code, not a boolean.
  modulrGbpSortCode: computed(() => State?.state?.company?.properties?.app_features?.modulr_gbp_sort_code ?? null),
  canViewSalarySettlementPage: isFlagEnabled('CAN_VIEW_SALARY_SETTLEMENT_PAGE'),
  canUploadMultiFile: isFlagEnabled('CAN_UPLOAD_MULTI_FILE'),
  // Hide salary view from portal admins/managers when the company has opted in
  // (company.properties.portal.hide_salary_view), unless this user has the
  // per-user override (user.properties.can_view_salary_on_portal). US-only on the
  // backend today; UK companies never set the flag so this stays a no-op there.
  // Salary is shown by default when the override is unset. Mirrors the backend
  // policy in salary_visibility.portal_salary_view_hidden.
  hideSalaryView: computed(() => {
    const claims = State.state?.claims;
    const isAdminOrManager = Boolean(claims?.a || claims?.m || claims?.is);
    if (!isAdminOrManager) {
      return false;
    }
    if (!State.state?.company?.properties?.portal?.hide_salary_view) {
      return false;
    }
    return !State.state?.user?.properties?.can_view_salary_on_portal;
  }),
};

const FeatureFlagWrapper = (defaultComponent, enabledComponent, flag) => ({
  render() {
    return h(flag in flags && flags[flag].value ? enabledComponent : defaultComponent);
  },
});

export default () => ({
  ...flags,
  FeatureFlagWrapper,
});
